import React, { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from './AuthContext';
import TabView from '../components/shared/TabView';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import ActivityList from './_activities/ActivityList';
import ActivitySelectionDialog from './_activities/ActivitySelectionModal';
import ActivityEditor from './_activities/ActivityEditor';
import Cookies from 'js-cookie';
import BomListManager from './_samsung/BomListManager';
import Queues from './Queues';
import { useActivity } from "./_activities/ActivityContext";
import { useLocation, useNavigate } from 'react-router-dom';
import MessagesViewer from './_activities/MessagesViewer'; // Import MessagesViewer

// Define all tab label arrays and constants at the top to avoid ReferenceError
const activitySmallTabLabels = ['Activity', 'Comments', 'Log', 'AR', 'Order'];
const samsungSmallTabLabels = ['Create SO', 'Repairs', 'BOM List'];
const queuesSmallTabLabels = ['Not started', 'Open', 'Waiting', 'Completed'];
const appleSmallTabLabels = []; // Sett til [] eller ['Tab1', 'Tab2'] hvis du har Apple-subtabs

function Activities() {
  // Context and state declarations
  const { userRole } = useContext(AuthContext);
  const { selectedActivity, setSelectedActivity } = useActivity();
  const [loading, setLoading] = useState(false);
  const [activityNumber, setActivityNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [imeiNumber, setImeiNumber] = useState('');
  const [customerReference, setCustomerReference] = useState('');
  const [activityData, setActivityData] = useState([]);
  const [multipleResults, setMultipleResults] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [statusSearch, setStatusSearch] = useState('');
  const [brands, setBrands] = useState([]);
  const lastSearchedActivityRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Get stored values
  const getStoredValue = (key, defaultValue) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (e) {
      console.warn("Kunne ikke hente lagret tab verdi:", e);
      return defaultValue;
    }
  };

  const [activeTab, setActiveTab] = useState(() => getStoredValue('activities_activeTab', 0));
  const [handlingActiveTab, setHandlingActiveTab] = useState(() => {
    const stored = getStoredValue('activities_handlingActiveTab', activitySmallTabLabels[0]);
    const val = activitySmallTabLabels.includes(stored) ? stored : activitySmallTabLabels[0];
    return val.toLowerCase();
  });
  const [queuesActiveTab, setQueuesActiveTab] = useState(() => {
    const stored = getStoredValue('activities_queuesActiveTab', queuesSmallTabLabels[0]);
    const val = queuesSmallTabLabels.includes(stored) ? stored : queuesSmallTabLabels[0];
    return val.toLowerCase();
  });
  const [appleActiveTab, setAppleActiveTab] = useState(() => {
    const stored = getStoredValue('activities_appleActiveTab', '');
    return stored ? stored.toLowerCase() : '';
  });
  const [samsungActiveTab, setSamsungActiveTab] = useState(() => {
    const stored = getStoredValue('activities_samsungActiveTab', 'create so');
    const val = samsungSmallTabLabels.includes(stored) ? stored : samsungSmallTabLabels[0];
    return val.toLowerCase();
  });

  // Define all helper functions first
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      searchForDevice(activityNumber);
    }
  };

  const handleInputChange = (e, field) => {
    const { value } = e.target;

    switch (field) {
      case 'activityNumber':
        setActivityNumber(value);
        setSerialNumber('');
        setImeiNumber('');
        setCustomerReference('');
        break;
      case 'serialNumber':
        setSerialNumber(value);
        setActivityNumber('');
        setImeiNumber('');
        setCustomerReference('');
        break;
      case 'imeiNumber':
        setImeiNumber(value);
        setActivityNumber('');
        setSerialNumber('');
        setCustomerReference('');
        break;
      case 'customerReference':
        setCustomerReference(value);
        setActivityNumber('');
        setSerialNumber('');
        setImeiNumber('');
        break;
      default:
        break;
    }
  };

  const handleSelectActivity = (activity) => {
    // Bytt til HANDLING tab (index 0)
    setActiveTab(0);
    // Bytt til Activity sub-tab
    setHandlingActiveTab('activity');

    // Reset dialog og søkefelt
    setShowDialog(false);
    setActivityNumber('');
    setSerialNumber('');
    setImeiNumber('');
    setCustomerReference('');

    // Remount editor og hent alltid fersk data fra backend
    setSelectedActivity(null);
    if (activity?.orderData?.activityNumber) {
      searchForDevice(activity.orderData.activityNumber);
    }

    // Legg til i activityData hvis den ikke finnes der fra før
    const exists = activityData.some(
      existingActivity => existingActivity.orderData?.activityNumber === activity.orderData?.activityNumber
    );
    if (!exists) {
      setActivityData(prevData => [activity, ...prevData]);
    }
  };

  const searchForDevice = async (actNumber) => {
    setLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL;
    let searchParam = actNumber ? `act_number=${actNumber}` : '';
    
    const url = `${apiUrl}/hub/search?${searchParam}`;
    const token = Cookies.get('token');
    
    if (!token) {
      alert('Du må være innlogget for å søke etter en serviceordre.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    
      if (response.status === 401) {
        alert('Token utløpt eller ugyldig. Vennligst logg inn på nytt.');
        setLoading(false);
        return;
      }
    
      if (response.ok) {
        const jsonResponse = await response.json();
        const activities = jsonResponse.data || [];
        
        if (activities.length > 0) {
          // If multiple cases are returned, select the one matching actNumber
          const matched = activities.find(item => 
            String(item.orderData?.activityNumber) === String(actNumber)
          );
          const finalData = matched || activities[0];
          
          if (String(finalData.orderData?.activityNumber) !== String(actNumber)) {
            console.warn("Returned activity number", finalData.orderData?.activityNumber, "does not match requested", actNumber);
            setLoading(false);
            return;
          }

          // Oppdater activityData listen hvis den ikke allerede finnes
          const exists = activityData.some(
            activity => activity.orderData?.activityNumber === finalData.orderData?.activityNumber
          );
    
          if (!exists) {
            setActivityData(prevData => [finalData, ...prevData]);
          }
          
          // Alltid oppdater selectedActivity via konteksten
          setSelectedActivity(finalData);
          
        } else {
          alert('Ingen saker funnet for det oppgitte søket.');
        }
      } else {
        alert(`Feil ved forespørsel: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Feil ved søk:', error);
    } finally {
      setLoading(false);
      // Clear search fields only if this wasn't triggered by handleSelectActivity
      if (lastSearchedActivityRef.current !== actNumber) {
        setActivityNumber('');
        setSerialNumber('');
        setImeiNumber('');
        setCustomerReference('');
      }
      lastSearchedActivityRef.current = null;
    }
  };
  
  // Funksjon for å lagre endringer til backend
  const handleSave = async (newData) => {
    console.log("Lagrer ny verdi:", newData);
  
    const apiUrl = process.env.REACT_APP_STATUS_API_URL;
    const updateUrl = `${apiUrl}/update_case`;  
    const token = Cookies.get('token');
  
    if (!token) {
      alert('Du må være innlogget for å oppdatere en sak.');
      return;
    }
  
    // Bygg strukturen for dataene i samsvar med det riktige formatet
    const payload = {
      guid: newData.guid,  // Unik identifikator for saken
      orderData: {
        activityNumber: newData.activityNumber,
        consents: newData.consents || {},
        providerSpecific: newData.providerSpecific || {},
        refNo: newData.refNo,
        shipmentData: newData.shipmentData || {},
        bookingType: newData.bookingType,
        serviceProviderExportStatus: newData.serviceProviderExportStatus,
        clientPostalCode: newData.clientPostalCode,
      },
      productData: {
        imei: newData.productImei,
        model: newData.productModel,
        serial: newData.productSerial,
        problemText: newData.problemText,
        purchaseDate: newData.purchaseDate,
      },
      customer: {
        name: newData.customerName,
        address: newData.customerAddress,
        postalCode: newData.customerPostalCode,
        city: newData.customerCity,
        email: newData.customerEmail,
        phone: newData.customerPhone,
      },
      tags: newData.tags || [],  // Pass på at tags sendes som en liste
    };
  
    try {
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),  // Send formaterte data til backend
      });
  
      if (response.ok) {
        const result = await response.json();
        alert('Saken er oppdatert!');
        console.log('Update result:', result);
      } else {
        alert(`Feil ved oppdatering: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
    }
  };
  
  const tabLabels = ['HANDLING', 'QUEUES']; // Base tabs, will be extended based on brands

  const spaceForEditor = (
    <Box sx={{ height: '300px', backgroundColor: '#636363', marginBottom: '5px' }} />
  );

  // Update activitySmallTabContents array to include MessagesViewer:
  const activitySmallTabContents = [
    loading ? (
      <CircularProgress />
    ) : selectedActivity ? (
      <div>
        <ActivityEditor 
           key={selectedActivity.guid || 'new-activity'} // Remount editor when selectedActivity changes
           initialData={selectedActivity} 
           onSubmit={handleSave} 
        />
      </div>
    ) : (
      <div />
    ),
    <MessagesViewer />, // Replace the dummy content with MessagesViewer
    <div>{spaceForEditor}</div>,
    <div>{spaceForEditor}</div>,
    <div>{spaceForEditor}</div>
  ];

  const samsungSmallTabContents = [
    <div>Innhold for Sub-tab 1</div>,
    <div>Innhold for Sub-tab 2</div>,
    <div>
      <BomListManager />
    </div>,
  ];

  const queuesSmallTabContents = [
    <Box key="queues" sx={{ 
      width: "100%",
      maxWidth: "100%",
      marginLeft: 0,
      padding: "0 0px",
      overflow: 'hidden',
      "& > div": {
        width: "100%!important",
        marginLeft: "0!important",
        justifyContent: "flex-start"
      }
    }}>
      <Queues endpoint='/activity/assigned' />
    </Box>,
    <Box key="open-queues" sx={{ 
      width: "100%",
      maxWidth: "100%",
      marginLeft: 0,
      padding: "0 0px",
      overflow: 'hidden',
      "& > div": {
        width: "100%!important",
        marginLeft: "0!important",
        justifyContent: "flex-start"
      }
    }}>
      <Queues endpoint='/activity/open-cases' />
    </Box>,
    <Box key="waiting-queues" sx={{
      width: "100%",
      maxWidth: "100%",
      marginLeft: 0,
      padding: "0 0px",
      overflow: 'hidden',
      "& > div": {
        width: "100%!important",
        marginLeft: "0!important",
        justifyContent: "flex-start"
      }
    }}>
      <Queues endpoint='/activity/waiting' />
    </Box>,
  ];

  // Lagre tab-state når den endres
  useEffect(() => {
    localStorage.setItem('activities_activeTab', JSON.stringify(activeTab));
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('activities_handlingActiveTab', JSON.stringify(handlingActiveTab));
  }, [handlingActiveTab]);

  useEffect(() => {
    localStorage.setItem('activities_queuesActiveTab', JSON.stringify(queuesActiveTab));
  }, [queuesActiveTab]);

  useEffect(() => {
    localStorage.setItem('activities_appleActiveTab', JSON.stringify(appleActiveTab));
  }, [appleActiveTab]);

  useEffect(() => {
    localStorage.setItem('activities_samsungActiveTab', JSON.stringify(samsungActiveTab));
  }, [samsungActiveTab]);

  // Hent brands fra cookies og sett i state
  useEffect(() => {
    const brandsCookie = Cookies.get('brands');
    if (brandsCookie) {
      try {
        const parsedBrands = JSON.parse(brandsCookie);
        if (Array.isArray(parsedBrands)) {
          setBrands(parsedBrands.map(b => b.toLowerCase()));
        }
      } catch (e) {
        // fallback hvis det er en streng
        setBrands(typeof brandsCookie === 'string' ? [brandsCookie.toLowerCase()] : []);
      }
    } else {
      setBrands([]);
    }
  }, []);

  // Initialiser tabs basert på URL-parametre ved oppstart
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const subTabParam = urlParams.get('subTab');
    // Hvis tab finnes i URL, sett riktig activeTab
    if (tabParam) {
      const tabIndex = tabLabels.findIndex(
        label => label.toLowerCase() === tabParam.toLowerCase()
      );
      if (tabIndex >= 0) {
        setActiveTab(tabIndex);
      }
    }
    // Hvis subTab finnes, oppdater riktig undertab-state basert på aktiv hovedtab
    const currentTab = tabParam ? 
      tabLabels.findIndex(label => label.toLowerCase() === tabParam.toLowerCase()) : 
      activeTab;
    switch(currentTab) {
      case 0: // HANDLING
        if (subTabParam) {
          const match = activitySmallTabLabels.find(l => l.toLowerCase() === subTabParam.toLowerCase());
          if (match) setHandlingActiveTab(match.toLowerCase());
        }
        break;
      case 1: // QUEUES
        if (subTabParam) {
          const match = queuesSmallTabLabels.find(l => l.toLowerCase() === subTabParam.toLowerCase());
          if (match) setQueuesActiveTab(match.toLowerCase());
        }
        break;
      case 2: // Kan være APPLE eller SAMSUNG avhengig fra brands
        if (subTabParam) {
          if (brands.includes('apple')) {
            const match = appleSmallTabLabels?.find?.(l => l.toLowerCase() === subTabParam.toLowerCase());
            if (match) setAppleActiveTab(match.toLowerCase());
          } else if (brands.includes('samsung')) {
            const match = samsungSmallTabLabels.find(l => l.toLowerCase() === subTabParam.toLowerCase());
            if (match) setSamsungActiveTab(match.toLowerCase());
          }
        }
        break;
      case 3: // Kan være SAMSUNG hvis APPLE allerede eksisterer
        if (subTabParam && brands.includes('apple') && brands.includes('samsung')) {
          const match = samsungSmallTabLabels.find(l => l.toLowerCase() === subTabParam.toLowerCase());
          if (match) setSamsungActiveTab(match.toLowerCase());
        }
        break;
      default:
        break;
    }
  }, [location.search, brands, tabLabels, activeTab]);

  // --- Add: Set search fields from URL param on mount ---
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      // Example: search=status=EstSent
      const match = searchParam.match(/^status=(.+)$/i);
      if (match) {
        // Optionally, clear other fields
        setActivityNumber('');
        setSerialNumber('');
        setImeiNumber('');
        setCustomerReference('');
        // Set a new state for status search (add if not present)
        setStatusSearch(match[1]);
      }
    }
  }, [location.search]);
  // --- END ---

  // Oppdater URL når underfane byttes
  const handleHandlingTabChange = (label) => {
    setHandlingActiveTab(label.toLowerCase());
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tabLabels[activeTab].toLowerCase());
    params.set('subTab', label.toLowerCase());
    navigate(`?${params.toString()}`, { replace: true });
  };
  const handleQueuesTabChange = (label) => {
    setQueuesActiveTab(label.toLowerCase());
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tabLabels[activeTab].toLowerCase());
    params.set('subTab', label.toLowerCase());
    navigate(`?${params.toString()}`, { replace: true });
  };
  const handleSamsungTabChange = (label) => {
    setSamsungActiveTab(label.toLowerCase());
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tabLabels[activeTab].toLowerCase());
    params.set('subTab', label.toLowerCase());
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Now define tabLabels and tabContents after all *TabContents
  const getValidTabIndex = (labels, value) => {
    const idx = labels.indexOf(value);
    return idx >= 0 ? idx : 0;
  };

  const tabContents = [
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TabView
        key={`handling-${handlingActiveTab}`}
        tabLabels={activitySmallTabLabels}
        tabContents={activitySmallTabContents}
        variant="small"
        customSmallDimensions={{
          minHeight: '0',
          maxHeight: 'none',
          flexGrow: 1,
        }}
        activeTab={handlingActiveTab}
        onTabChange={handleHandlingTabChange}
      />

      Recently opened activities:
      <ActivityList
        activities={activityData}
        onSelectActivity={handleSelectActivity}
        searchForDevice={searchForDevice}
        setActivityData={setActivityData}
      />
    </div>,
    <div style={{ height: '100%' }}>
      <TabView
        key={`queues-${queuesActiveTab}`}
        tabLabels={queuesSmallTabLabels} 
        tabContents={queuesSmallTabContents} 
        variant="small"
        activeTab={queuesActiveTab}
        onTabChange={handleQueuesTabChange}
      />
    </div>
  ];

  if (brands.includes('apple')) {
    tabLabels.push('APPLE');
    tabContents.push(
      <div>
        {/* Hvis Apple har under-tabs, kan du legge til en TabView her med appleActiveTab */}
        Apple Cases
      </div>
    );
  }

  if (brands.includes('samsung')) {
    tabLabels.push('SAMSUNG');
    tabContents.push(<div>
      <TabView 
        tabLabels={samsungSmallTabLabels} 
        tabContents={samsungSmallTabContents} 
        variant="small" 
        activeTab={samsungActiveTab}
        onTabChange={handleSamsungTabChange}
      />
    </div>);
  }

  // Håndterer bytte mellom hoved-tabs
  const handleMainTabChange = (index) => {
    let numericIndex;
    if (typeof index === 'string') {
      numericIndex = tabLabels.findIndex(label => label.toLowerCase() === index.toLowerCase());
    } else {
      numericIndex = index;
    }
    if (typeof numericIndex !== 'number' || numericIndex < 0 || numericIndex >= tabLabels.length) {
      console.warn('Invalid tab index:', numericIndex);
      return;
    }
    setActiveTab(numericIndex);

    // Oppdater URL med valgt hovedfane og underfane
    const mainTabLabel = tabLabels[numericIndex].toLowerCase();
    let subTabLabel = '';
    switch (numericIndex) {
      case 0:
        subTabLabel = handlingActiveTab;
        break;
      case 1:
        subTabLabel = queuesActiveTab;
        break;
      case 2:
        if (brands.includes('apple')) subTabLabel = appleActiveTab;
        else if (brands.includes('samsung')) subTabLabel = samsungActiveTab;
        break;
      case 3:
        if (brands.includes('apple') && brands.includes('samsung')) subTabLabel = samsungActiveTab;
        break;
      default:
        break;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('tab', mainTabLabel);
    if (subTabLabel) params.set('subTab', subTabLabel);
    else params.delete('subTab');
    navigate(`?${params.toString()}`, { replace: true });

    // Sett riktig underfane for valgt hovedtab
    switch (numericIndex) {
      case 0: // HANDLING
        setHandlingActiveTab(prev => {
          // Hvis tidligere valgt finnes, behold den, ellers bruk første
          return activitySmallTabLabels.includes(prev) ? prev : activitySmallTabLabels[0];
        });
        break;
      case 1: // QUEUES
        setQueuesActiveTab(prev => {
          return queuesSmallTabLabels.includes(prev) ? prev : queuesSmallTabLabels[0];
        });
        break;
      case 2:
        if (brands.includes('apple')) {
          setAppleActiveTab(prev => {
            return appleSmallTabLabels?.includes?.(prev) ? prev : (appleSmallTabLabels[0] || '');
          });
        } else if (brands.includes('samsung')) {
          setSamsungActiveTab(prev => {
            return samsungSmallTabLabels.includes(prev) ? prev : samsungSmallTabLabels[0];
          });
        }
        break;
      case 3:
        if (brands.includes('apple') && brands.includes('samsung')) {
          setSamsungActiveTab(prev => {
            return samsungSmallTabLabels.includes(prev) ? prev : samsungSmallTabLabels[0];
          });
        }
        break;
      default:
        break;
    }
  };

  // Lytt etter custom event 'activity-queue-select' og oppdater søkefeltet, bytt til Handling/Activity, og start søk automatisk når eventen trigges. Dette gir ønsket oppførsel ved dobbelklikk fra Queues.
  useEffect(() => {
    // Lytt etter custom event fra Queues
    const handler = (e) => {
      const { activityNumber } = e.detail || {};
      if (activityNumber) {
        setActivityNumber(activityNumber);
        setActiveTab(0); // HANDLING
        setHandlingActiveTab('activity');
        searchForDevice(activityNumber);
      }
    };
    window.addEventListener('activity-queue-select', handler);
    return () => window.removeEventListener('activity-queue-select', handler);
  }, []);

  return (
    <div>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap', // Tillater ombrytning på mindre skjermer
        mb: 2 // Margin bottom for å skape avstand til TabView
      }}>
        <h1>Activities</h1>
        
        {/* Søkefelter flyttet hit fra Handling-tabben */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexGrow: 1,
          maxWidth: { xs: '100%', md: '80%' },
          ml: { xs: 0, md: 2 }
        }}>
          <TextField
            id="actNumber-search"
            label="Act Number"
            variant="outlined"
            size="small"
            value={activityNumber}
            onChange={(e) => handleInputChange(e, 'activityNumber')}
            onKeyPress={handleKeyPress}
            sx={{ input: { color: '#fff' }, label: { color: '#c5c5c5' } }}
          />
        </Box>
      </Box>

      <TabView 
        tabLabels={tabLabels} 
        tabContents={tabContents}
        activeTab={activeTab}
        onTabChange={(tabName, index) => handleMainTabChange(index !== undefined ? index : tabLabels.indexOf(tabName))}
      />

      {showDialog && (
        <ActivitySelectionDialog
          activities={multipleResults}
          onSelectActivity={handleSelectActivity}
          onClose={() => setShowDialog(false)}
        />
      )}
    </div>
  );
}

export default Activities;
